import { MediaType, Position } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { css } from '@lastbrand/web-ui-styled-system/css'

import { RichTextDS } from '@src/components/blocks/rich-text-ds'
import { VideoDS, VIDEO_LOADING_TYPE, VIDEO_PRELOAD_TYPE } from '@src/components/blocks/video-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Image } from '@src/quince-components/src/atoms/image'

import type { MediaTextMosaicDSProps, MediaTextMosaicRow } from './media-text-mosaic-ds.types'
import type { Media } from '@lastbrand/storefront-proto-registry/api/v1/widget'
import type { ReactNode } from 'react'

/**
 * DS MEDIA_TEXT_MOSAIC: 50/50 media + text rows (no side gutters; 1600 desktop cap).
 * Media fills a cell — never put width:50% on Image or flex blows past half.
 *
 * Geometry matches the Figma "Mosaic" frames, the same spec the non-PL twin
 * (`atoms/featureSectionEight/feature-section-eight-ds.tsx`) implements:
 *   desktop 6998:22800 (1600×800) · 6967:25526 (1200×600) · mobile 6967:25527 (360) · 6998:23236 (520)
 */

const sectionClass = css({ width: 'full' })

// Figma `Content` carries `max-w-[1600px]`.
const contentFrameClass = css({
  display: 'flex',
  flexDirection: 'column',
  width: 'full',
  maxWidth: '1600px',
  marginInline: 'auto',
})

const rowClass = css({
  display: 'flex',
  alignItems: 'stretch',
  width: 'full',
  flexDirection: 'column',
  lg: { flexDirection: 'row' },
})

const rowReverseClass = css({
  display: 'flex',
  alignItems: 'stretch',
  width: 'full',
  flexDirection: 'column',
  lg: { flexDirection: 'row-reverse' },
})

const mediaCellClass = css({
  width: '100%',
  minWidth: 0,
  lg: { width: '50%', flex: '0 0 50%' },
})

// Ratio-driven at both breakpoints so the media never turns portrait: `390 / 290` on mobile (Figma
// 6967:25527 and 6998:23236 `Image`, both `aspect-[390/290]` — 360×267.69 and 520×386.66) and a 1:1 square
// on desktop (6998:22801, `aspect-[400/400]`) — 800×800 in the 1600 frame, 600×600 in the 1200 frame.
// `lightgray` is the pre-load placeholder.
//
// NOTE: this overrides develop's "mobile keeps the asset's natural ratio (no forced crop)" from #7487. Both
// mobile frames pin the ratio explicitly, and an unconstrained box makes row height vary per asset, so the
// spec is followed here — but it IS a deliberate reversal of that call, flagged for the reviewer.
const mediaFrameClass = css({
  width: '100%',
  aspectRatio: '390 / 290',
  overflow: 'hidden',
  backgroundColor: 'lightgray',
  lg: { aspectRatio: '1 / 1' },
})

const mediaFillClass = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
})

// The 50% column, and the carrier for any CMS panel background.
const textCellClass = css({
  width: '100%',
  minWidth: 0,
  lg: { width: '50%', flex: '0 0 50%' },
})

/**
 * The Figma `Body`. Kept as a child of the cell rather than merged into it because the desktop inset is a
 * PERCENTAGE: on the cell itself, `20%` would resolve against the flex container (the full row, 1600) and
 * land at 320px; on this child it resolves against the cell (the half, 800) and lands at the designed 160.
 *
 *  - mobile  — 6967:25527 `Body`: `pt-32 pb-56 px-16`, top-aligned.
 *  - desktop — 6998:22804 `Body`: a `1fr 3fr 1fr` grid with the copy in column 2 (20% / 60% / 20%),
 *              vertically centred. Proportional, so it lands 160/480 at 1600 and 120/360 at 1200.
 *
 * The `Header` is a margin-free column with a 16px gap in every frame, so the gap lives here and the
 * children carry none — which is also what lets `justifyContent: center` hit the body's true midpoint.
 *
 * The 20% supersedes develop's fixed `paddingInline: 160px` from #7487: 160 is only correct on the 1600
 * frame, and the 1200 frame (6967:25526) specifies a 120px inset, which the proportional value hits.
 */
const textInnerClass = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  gap: 'space-16',
  width: '100%',
  paddingLeft: 'space-16',
  paddingRight: 'space-16',
  paddingTop: 'space-32',
  paddingBottom: 'space-56',
  lg: {
    height: 'full',
    justifyContent: 'center',
    paddingLeft: '20%',
    paddingRight: '20%',
    paddingTop: '0',
    paddingBottom: '0',
  },
})

// mobile 6967:25527 `fontsize/24` leading 1.18 → `title-24-dec` (ivy-headline 24px/28px);
// desktop 6998:22806 `fontsize/28` leading 1.2 → `title-28-dec` (28px/34px). `w-full` in both frames.
const titleClass = css({
  margin: '0',
  textStyle: 'title-24-dec',
  color: 'black',
  width: '100%',
  lg: { textStyle: 'title-28-dec' },
})

// `body/16` (Grosa 16px/22px) and `neutral/black` in BOTH frames, `w-full`. Static css() via `className`
// (not RichTextDS `baseStyle`): baseStyle rides a runtime `css(var)` Panda can't statically extract.
const bodyClass = css({
  textStyle: 'body-16',
  color: 'black',
  // `w-full`, not develop's `maxWidth: 30rem` — the 3fr column already caps it at the designed 480 on the
  // 1600 frame, and a fixed 30rem cap would not narrow to the 1200 frame's 360.
  width: '100%',
})

function wrapMedia(node: ReactNode) {
  return (
    <div className={mediaCellClass}>
      <div className={mediaFrameClass}>{node}</div>
    </div>
  )
}

function MosaicMedia({ media }: Readonly<{ media?: Media }>) {
  const content = media?.content
  if (!content?.url) return null

  // Proto media URLs are protocol-relative — prepend the scheme so the CDN transform resolves.
  const url = content.url.startsWith('//') ? `https:${content.url}` : content.url

  if (media?.type === MediaType.VIDEO) {
    // `VideoDS` owns the media frame here (rather than nesting inside `wrapMedia`) so its wrapper is the
    // positioned box the Figma play/pause control anchors to. The url is passed RAW — `VideoDS` does its own
    // Contentful→Quince CDN rewrite plus the protocol prefix, so pre-resolving it would double-apply.
    return (
      <div className={mediaCellClass}>
        <VideoDS
          url={content.url}
          contentType={content.contentType}
          title={content.alt ?? undefined}
          muted
          autoplay
          loop
          limitedControls
          loadingType={VIDEO_LOADING_TYPE.Lazy}
          preload={VIDEO_PRELOAD_TYPE.Auto}
          wrapperClassName={mediaFrameClass}
          className={mediaFillClass}
        />
      </div>
    )
  }

  // SVGs bypass the raster CDN pipeline — render as a plain <img>.
  if (/\.svg(\?|#|$)/i.test(url)) {
    return wrapMedia(
      <img
        className={mediaFillClass}
        src={url}
        alt={content.alt ?? ''}
        loading="lazy"
        width={content.width || undefined}
        height={content.height || undefined}
      />,
    )
  }

  return wrapMedia(
    <Image
      url={url}
      title={content.alt ?? ''}
      alt={content.alt ?? ''}
      className={mediaFillClass}
      loading="lazy"
      width={content.width || undefined}
      height={content.height || undefined}
    />,
  )
}

function MosaicRow({ row, index }: Readonly<{ row: MediaTextMosaicRow; index: number }>) {
  return (
    <div className={row.position === Position.RIGHT ? rowReverseClass : rowClass}>
      <MosaicMedia media={row.media} />
      <div className={textCellClass} style={row.backgroundColor ? { backgroundColor: row.backgroundColor } : undefined}>
        <div className={textInnerClass}>
          {row.title ? (
            <h3 className={titleClass} data-testid={TEST_LOCATORS.MEDIA_TEXT_MOSAIC.TITLE(index)}>
              {row.title}
            </h3>
          ) : null}
          {row.subTitle ? <RichTextDS content={row.subTitle} className={bodyClass} /> : null}
        </div>
      </div>
    </div>
  )
}

// STRFRNT-16852: the PDP section dividers (`product-page-pl-renderer.styles.ts`) key off this marker
// to skip the hairline directly below the section — where a band paints its own background, the colour
// change at its bottom edge IS the separation, and a line under it reads as a second divider.
//
// Only the LAST row matters: it owns the section's bottom edge, which is the edge the divider abuts.
// An authored white band is the page colour, so it still wants the line.
const PAGE_BACKGROUND_COLOR = '#ffffff'

function paintsBottomBand(rows: MediaTextMosaicRow[]): boolean {
  const bottomBackground = rows.at(-1)?.backgroundColor
  return bottomBackground !== undefined && bottomBackground !== PAGE_BACKGROUND_COLOR
}

export function MediaTextMosaicDS({ rows, testId }: MediaTextMosaicDSProps) {
  if (rows.length === 0) return null
  return (
    <section className={sectionClass} data-testid={testId} data-section-band={paintsBottomBand(rows) ? '' : undefined}>
      <div className={contentFrameClass}>
        {rows.map((row, index) => (
          <MosaicRow key={row.key} row={row} index={index} />
        ))}
      </div>
    </section>
  )
}
